import { useEffect, useRef, useState } from 'react';
import { PlayCircle, X, CheckCircle2, Youtube, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { villageUx } from '../lib/api';

const STEPS = [
  'Tap “Stock it” on the items you sell',
  'Change the price if your rate is different',
  'Keep your phone on — new orders ring here',
  'Hand over the bag & collect COD / UPI',
];

export default function MerchantTutorialVideo() {
  const [hidden, setHidden] = useState(() => localStorage.getItem('ms_tutorial_hidden') === '1');
  const [videoId, setVideoId] = useState(null);
  useEffect(() => { villageUx.tutorial().then((r) => setVideoId(r.video_id)).catch(() => {}); }, []);

  if (hidden) {
    return (
      <button data-testid="merchant-tutorial-show" onClick={() => { localStorage.removeItem('ms_tutorial_hidden'); setHidden(false); }}
        className="pill !bg-white text-xs mt-4"><PlayCircle className="w-3.5 h-3.5"/>Watch how BharatCart works</button>
    );
  }
  return (
    <section data-testid="merchant-tutorial" className="mt-6 rounded-2xl border-2 border-ink/10 bg-white overflow-hidden md:grid md:grid-cols-5">
      <div className="md:col-span-3 aspect-video bg-ink relative">
        {videoId ? (
          <iframe data-testid="merchant-tutorial-iframe" className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
            title="BharatCart merchant tutorial" allow="accelerometer; encrypted-media; picture-in-picture" allowFullScreen/>
        ) : (
          <div data-testid="merchant-tutorial-placeholder" className="absolute inset-0 grid place-items-center text-white text-center p-6">
            <div>
              <PlayCircle className="w-16 h-16 mx-auto text-[#E67E22]"/>
              <div className="font-heading font-black text-xl mt-2">2-minute shop setup video</div>
              <div className="text-xs text-white/60 mt-1">Video coming soon — follow the 4 steps alongside.</div>
            </div>
          </div>
        )}
      </div>
      <div className="md:col-span-2 p-4 relative">
        <button data-testid="merchant-tutorial-hide" onClick={() => { localStorage.setItem('ms_tutorial_hidden', '1'); setHidden(true); }}
          className="absolute top-3 right-3 text-ink/40 hover:text-ink" aria-label="Hide tutorial"><X className="w-4 h-4"/></button>
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#D35400]">New here?</div>
        <div className="font-heading font-black text-lg">Start selling in 4 steps</div>
        <ol className="mt-3 space-y-2">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-[#1B5E20] mt-0.5 shrink-0"/><span><b>{i + 1}.</b> {s}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function AdminTutorialSetting() {
  const [url, setUrl] = useState('');
  const [saved, setSaved] = useState(null);
  const touched = useRef(false);
  useEffect(() => {
    villageUx.tutorial().then((r) => { setSaved(r); if (!touched.current) setUrl(r.url || ''); }).catch(() => {});
  }, []);
  const save = async (value = url) => {
    try {
      const r = await villageUx.setTutorial(value);
      setSaved(r);
      setUrl(r.url || '');
      toast.success(r.video_id ? 'Tutorial video updated for all merchants' : 'Tutorial video removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save link'); }
  };
  return (
    <div data-testid="admin-tutorial-setting" className="mt-6 rounded-2xl border-2 border-ink/10 bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <Youtube className="w-5 h-5 text-[#B71C1C]"/>
        <div>
          <div className="font-bold text-sm">Merchant tutorial video</div>
          <div data-testid="admin-tutorial-status" className="text-[11px] text-ink/50">
            {saved?.video_id ? `Live · ${saved.video_id}` : 'Not set — merchants see a placeholder'}
          </div>
        </div>
      </div>
      <input data-testid="admin-tutorial-url" value={url} onChange={(e) => { touched.current = true; setUrl(e.target.value); }}
        placeholder="Paste YouTube link, e.g. https://youtu.be/XXXXXXXXXXX"
        className="flex-1 min-w-0 border-2 border-ink/15 rounded-lg px-3 h-10 text-sm outline-none focus:border-ink"/>
      <button data-testid="admin-tutorial-save" onClick={() => save()} className="btn-primary !py-2 !px-4 text-sm"><Save className="w-4 h-4"/>Save</button>
      {saved?.video_id && (
        <button data-testid="admin-tutorial-clear" onClick={() => save('')} className="btn-outline !py-2 !px-4 text-sm"><Trash2 className="w-4 h-4"/>Remove</button>
      )}
    </div>
  );
}
